import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ChevronLeft, ChevronRight, Plus, RefreshCw, RotateCcw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { YesNoDialog } from '@/components/ui/modules/yes-no-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { UnifiedTable, UnifiedTableColumn } from '@/components/ui/unifiedTable/UnifiedTable';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MedcallClientRowActions, MedcallDashboardCidLink, MedcallMedicationCountPopover } from './MedcallWorkspaceRowControls';
import { MedcallParticipantIntakeDialog } from '../medcall/MedcallParticipantIntakeDialog';
import { MedcallWorkspaceMobileClients } from './MedcallWorkspaceMobileClients';
import { MedcallOperatorReportsMenu } from '../medcall/MedcallOperatorReportsMenu';
import { MedcallSendMessageDialog } from '../medcall/MedcallSendMessageDialog';
const MEDCALL_CLIENT_LIST_PAGE = gql `
  query MedcallClientListPage(
    $facilityFilters: [Int]
    $firstnameSearchText: String
    $lastnameSearchText: String
    $nurseSearchText: String
    $page: Int
    $pageSize: Int
    $preserveLegacyMyClientsListFilterSemantics: Boolean
    $preserveLegacyMyClientsNurseFilter: Boolean
    $programFilters: [String]
    $providerFilters: [String]
    $scope: String
    $searchText: String
    $sortDirection: String
    $sortField: String
    $status: String
  ) {
    medcallParticipantListMeta {
      facilityOptions {
        label
        value
      }
      programOptions {
        label
        value
      }
      providerOptions {
        label
        value
      }
    }
    medcallParticipantsList(
      facilityFilters: $facilityFilters
      firstnameSearchText: $firstnameSearchText
      lastnameSearchText: $lastnameSearchText
      nurseSearchText: $nurseSearchText
      page: $page
      pageSize: $pageSize
      preserveLegacyMyClientsListFilterSemantics: $preserveLegacyMyClientsListFilterSemantics
      preserveLegacyMyClientsNurseFilter: $preserveLegacyMyClientsNurseFilter
      programFilters: $programFilters
      providerFilters: $providerFilters
      scope: $scope
      searchText: $searchText
      sortDirection: $sortDirection
      sortField: $sortField
      status: $status
    ) {
      totalCount
      items {
        active
        active_medication_names
        active_medications_count
        aliases {
          firstname
          lastname
        }
        cid
        created_at
        dob
        facility
        firstname
        id
        lastname
        nurse_name {
          email
          firstname
          lastname
          title
        }
        participant_id
        program
        provider
        staff_assigned
        view_user_ids
      }
    }
  }
`;
const MEDCALL_ADD_TO_MY_CLIENTS = gql `
  mutation MedcallAddToMyClients($participantId: Int!) {
    medcallAddToMyClients(participantId: $participantId) {
      message
      responseCode
      responseID
      extra
    }
  }
`;
const MEDCALL_REMOVE_FROM_MY_CLIENTS = gql `
  mutation MedcallRemoveFromMyClients($participantId: Int!) {
    medcallRemoveFromMyClients(participantId: $participantId) {
      message
      responseCode
      responseID
      extra
    }
  }
`;
const PAGE_SIZE = 25;
const EMPTY_FILTER_OPTIONS: MedcallOption[] = [];
const NURSELESS_PROGRAMS = ['ODRH Scattered/ERC', 'FIST ERC', 'DSH ERC'];
const PROGRAM_LEVEL_MANAGEMENT_PROGRAMS = [...NURSELESS_PROGRAMS, 'Project Sites'];
type MedcallListScope = 'all' | 'my';
type MedcallBootstrapContext = {
    canViewMonitoringReports: boolean;
    hasSuperAccess: boolean;
    isAdminUser: boolean;
    medcallUserId: number;
};
type MedcallOption = {
    label: string;
    value: string;
};
type MedcallPerson = {
    email?: string | null;
    firstname?: string | null;
    lastname?: string | null;
    title?: string | null;
};
export type MedcallParticipantItem = {
    active?: boolean | null;
    active_medication_names?: string[] | null;
    active_medications_count?: number | null;
    aliases?: Array<{
        firstname?: string | null;
        lastname?: string | null;
    }> | null;
    cid?: string | null;
    created_at?: string | null;
    dob?: string | null;
    facility?: string | null;
    firstname?: string | null;
    id: number;
    lastname?: string | null;
    nurse_name?: MedcallPerson | null;
    participant_id?: number | null;
    program?: string | null;
    provider?: string | null;
    staff_assigned?: string | null;
    view_user_ids?: string[] | null;
};
type MedcallClientListPageData = {
    medcallParticipantListMeta: {
        facilityOptions: MedcallOption[];
        programOptions: MedcallOption[];
        providerOptions: MedcallOption[];
    };
    medcallParticipantsList: {
        items: MedcallParticipantItem[];
        totalCount: number;
    };
};
type MultiSelectFilterProps = {
    label: string;
    onChange: (values: string[]) => void;
    options: MedcallOption[];
    selectedValues: string[];
};
type ActiveFilterChip = {
    key: string;
    label: string;
    onClear: () => void;
};
const buildFilterButtonLabel = (label: string, options: MedcallOption[], selectedValues: string[]) => {
    if (selectedValues.length < 1) {
        return label;
    }
    const selectedLabels = options
        .filter((option) => selectedValues.includes(option.value))
        .map((option) => option.label);
    if (selectedValues.length === 1) {
        return `${label}: ${selectedLabels[0] || selectedValues[0]}`;
    }
    return `${label}: ${selectedValues.length}`;
};
const formatDateLabel = (value: string | null | undefined) => {
    if (!value) {
        return '';
    }
    const match = `${value}`.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        return `${match[2]}/${match[3]}/${match[1]}`;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return `${value}`;
    }
    return parsed.toLocaleDateString();
};
const formatPersonLabel = (person: MedcallPerson | null | undefined, program: string | null | undefined) => {
    if (program && NURSELESS_PROGRAMS.includes(program)) {
        return 'N/A';
    }
    if (!person) {
        return '';
    }
    const pieces = [person.firstname, person.lastname].filter(Boolean);
    const title = person.title?.trim();
    if (title) {
        pieces.push(title);
    }
    return pieces.join(' ').trim();
};
const MultiSelectFilter = ({ label, onChange, options, selectedValues }: MultiSelectFilterProps) => {
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
    return (<DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="h-8 whitespace-nowrap" size="sm" type="button" variant="outline">
          {buildFilterButtonLabel(label, options, selectedValues)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto rounded-[var(--border-radius-medium)] border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] shadow-[var(--shadow-medium)]">
        <DropdownMenuLabel className="text-sm font-semibold text-[var(--neutral-gray-120)]">{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.length < 1 ? (<div className="px-2 py-2 text-sm text-[var(--neutral-gray-100)]">No options</div>) : null}
        {options.map((option) => (<DropdownMenuCheckboxItem key={`${label}-${option.value}`} checked={selectedSet.has(option.value)} onCheckedChange={(checked) => {
                const isChecked = checked === true;
                if (isChecked) {
                    onChange([...selectedValues, option.value].filter((value, index, values) => values.indexOf(value) === index));
                    return;
                }
                onChange(selectedValues.filter((value) => value !== option.value));
            }}>
            {option.label}
          </DropdownMenuCheckboxItem>))}
      </DropdownMenuContent>
    </DropdownMenu>);
};
export const MedcallWorkspaceClientList = ({ bootstrap, routeBase = '/tools/medcall-workspace', scope, }: {
    bootstrap: MedcallBootstrapContext;
    routeBase?: string;
    scope: MedcallListScope;
}) => {
    const navigate = useNavigate();
    const [status, setStatus] = useState<'active' | 'all'>('active');
    const [page, setPage] = useState(1);
    const [searchDraft, setSearchDraft] = useState('');
    const [searchText, setSearchText] = useState('');
    const [sortField, setSortField] = useState('firstname');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [programFilters, setProgramFilters] = useState<string[]>([]);
    const [providerFilters, setProviderFilters] = useState<string[]>([]);
    const [facilityFilters, setFacilityFilters] = useState<string[]>([]);
    const [firstnameSearchText, setFirstnameSearchText] = useState('');
    const [lastnameSearchText, setLastnameSearchText] = useState('');
    const [nurseSearchText, setNurseSearchText] = useState('');
    const [removeTarget, setRemoveTarget] = useState<MedcallParticipantItem | null>(null);
    const [messageTarget, setMessageTarget] = useState<MedcallParticipantItem | null>(null);
    const [pendingParticipantId, setPendingParticipantId] = useState<number | null>(null);
    const [intakeDialogOpen, setIntakeDialogOpen] = useState(false);
    const { data, error, loading, refetch } = useQuery<MedcallClientListPageData>(MEDCALL_CLIENT_LIST_PAGE, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: {
            facilityFilters: facilityFilters.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0),
            firstnameSearchText: null,
            lastnameSearchText: null,
            nurseSearchText: nurseSearchText.trim() || null,
            page,
            pageSize: PAGE_SIZE,
            preserveLegacyMyClientsListFilterSemantics: false,
            preserveLegacyMyClientsNurseFilter: false,
            programFilters: programFilters.length > 0 ? programFilters : null,
            providerFilters: providerFilters.length > 0 ? providerFilters : null,
            scope,
            searchText: searchText.trim() || null,
            sortDirection,
            sortField,
            status,
        },
    });
    const [addToMyClients] = useMutation(MEDCALL_ADD_TO_MY_CLIENTS);
    const [removeFromMyClients] = useMutation(MEDCALL_REMOVE_FROM_MY_CLIENTS);
    const items = data?.medcallParticipantsList?.items || [];
    const totalCount = data?.medcallParticipantsList?.totalCount || 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const canGoBack = page > 1;
    const canGoNext = page < totalPages;
    const meta = data?.medcallParticipantListMeta;
    const programOptions = meta?.programOptions || EMPTY_FILTER_OPTIONS;
    const providerOptions = meta?.providerOptions || EMPTY_FILTER_OPTIONS;
    const facilityOptions = meta?.facilityOptions || EMPTY_FILTER_OPTIONS;
    useEffect(() => {
        if (loading) {
            return;
        }
        if (page <= totalPages) {
            return;
        }
        setPage(totalPages);
    }, [loading, page, totalPages]);
    const handleSearchSubmit = (value: string) => {
        setPage(1);
        setSearchText(value.trim());
    };
    const handleReset = () => {
        setFacilityFilters([]);
        setFirstnameSearchText('');
        setLastnameSearchText('');
        setNurseSearchText('');
        setPage(1);
        setProgramFilters([]);
        setProviderFilters([]);
        setSearchDraft('');
        setSearchText('');
        setSortDirection('desc');
        setSortField('firstname');
        setStatus('active');
    };
    const handleRefresh = async () => {
        await refetch();
    };
    const handlePreviousPage = () => {
        if (!canGoBack) {
            return;
        }
        setPage((currentPage) => Math.max(1, currentPage - 1));
    };
    const handleNextPage = () => {
        if (!canGoNext) {
            return;
        }
        setPage((currentPage) => Math.min(totalPages, currentPage + 1));
    };
    const handleScopeChange = (value: MedcallListScope) => {
        if (value === scope) {
            return;
        }
        if (value === 'my') {
            navigate(`${routeBase}/my-clients`);
            return;
        }
        navigate(`${routeBase}/all-clients`);
    };
    const handleAddToMyClients = async (participantId: number) => {
        setPendingParticipantId(participantId);
        try {
            const result = await addToMyClients({
                variables: {
                    participantId,
                },
            });
            const response = result.data?.medcallAddToMyClients;
            if (response?.responseCode === 200) {
                toast.success(response.message || 'Client added');
                await refetch();
                setPendingParticipantId(null);
                return;
            }
            toast.error(response?.message || 'Unable to add client');
        }
        catch (mutationError: any) {
            toast.error(mutationError?.message || 'Unable to add client');
        }
        setPendingParticipantId(null);
    };
    const handleConfirmRemove = async () => {
        if (!removeTarget) {
            return;
        }
        const participantId = removeTarget.id;
        setPendingParticipantId(participantId);
        try {
            const result = await removeFromMyClients({
                variables: {
                    participantId,
                },
            });
            const response = result.data?.medcallRemoveFromMyClients;
            if (response?.responseCode === 200) {
                toast.success(response.message || 'Client removed');
                setRemoveTarget(null);
                await refetch();
                setPendingParticipantId(null);
                return;
            }
            toast.error(response?.message || 'Unable to remove client');
        }
        catch (mutationError: any) {
            toast.error(mutationError?.message || 'Unable to remove client');
        }
        setPendingParticipantId(null);
    };
    const columns = useMemo<UnifiedTableColumn<MedcallParticipantItem>[]>(() => ([
        {
            field: 'firstname',
            header: 'Firstname',
            key: 'firstname',
            sortable: true,
            width: '10rem',
        },
        {
            field: 'lastname',
            header: 'Lastname',
            key: 'lastname',
            sortable: true,
            width: '10rem',
        },
        {
            field: 'participant_id',
            header: 'ID',
            key: 'participant_id',
            sortable: true,
            width: '6rem',
        },
        {
            accessor: (row) => (<MedcallDashboardCidLink cid={row.cid} firstname={row.firstname} lastname={row.lastname} participantId={row.id}/>),
            field: 'cid',
            header: 'CID',
            key: 'cid',
            sortable: true,
            width: '6rem',
        },
        {
            accessor: (row) => formatDateLabel(row.dob),
            field: 'dob',
            header: 'DOB',
            key: 'dob',
            sortable: true,
            width: '7rem',
        },
        {
            field: 'program',
            header: 'Program',
            key: 'program',
            sortable: true,
            width: '12rem',
            wrap: true,
            wrapAfterChars: 18,
        },
        {
            field: 'provider',
            header: 'Provider',
            key: 'provider',
            sortable: true,
            width: '10rem',
        },
        {
            field: 'facility',
            header: 'Facility',
            key: 'facility',
            sortable: true,
            width: '15rem',
            wrap: true,
            wrapAfterChars: 24,
        },
        {
            accessor: (row) => (<MedcallMedicationCountPopover medicationCount={row.active_medications_count} medicationNames={row.active_medication_names} onViewAll={() => {
                    navigate(`${routeBase}/participants/${row.id}`, {
                        state: {
                            from: scope === 'my' ? `${routeBase}/my-clients` : `${routeBase}/all-clients`,
                        },
                    });
                }} participantId={row.id}/>),
            header: 'T.Medications',
            key: 'active_medications_count',
            sortValue: (row) => row.active_medications_count || 0,
            sortable: true,
            width: '8rem',
        },
        {
            field: 'staff_assigned',
            header: 'Staff Assigned',
            key: 'staff_assigned',
            sortable: true,
            width: '11rem',
            wrap: true,
            wrapAfterChars: 20,
        },
        {
            accessor: (row) => formatPersonLabel(row.nurse_name, row.program),
            header: 'Nurse',
            key: 'nurse_name',
            sortable: true,
            width: '11rem',
            wrap: true,
            wrapAfterChars: 20,
        },
    ]), [navigate, routeBase, scope]);
    const rangeStart = totalCount < 1 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = totalCount < 1 ? 0 : Math.min(totalCount, page * PAGE_SIZE);
    const hasActiveFilters = status !== 'active' ||
        Boolean(searchText.trim()) ||
        Boolean(firstnameSearchText.trim()) ||
        Boolean(lastnameSearchText.trim()) ||
        programFilters.length > 0 ||
        providerFilters.length > 0 ||
        facilityFilters.length > 0 ||
        Boolean(nurseSearchText.trim());
    const activeFilterChips: ActiveFilterChip[] = [];
    if (status !== 'active') {
        activeFilterChips.push({
            key: 'status',
            label: 'Status: All',
            onClear: () => {
                setPage(1);
                setStatus('active');
            },
        });
    }
    if (searchText.trim()) {
        activeFilterChips.push({
            key: 'search',
            label: `Search: ${searchText.trim()}`,
            onClear: () => {
                setSearchDraft('');
                handleSearchSubmit('');
            },
        });
    }
    if (firstnameSearchText.trim()) {
        activeFilterChips.push({
            key: 'firstname',
            label: `Firstname: ${firstnameSearchText.trim()}`,
            onClear: () => {
                setPage(1);
                setFirstnameSearchText('');
            },
        });
    }
    if (lastnameSearchText.trim()) {
        activeFilterChips.push({
            key: 'lastname',
            label: `Lastname: ${lastnameSearchText.trim()}`,
            onClear: () => {
                setPage(1);
                setLastnameSearchText('');
            },
        });
    }
    if (programFilters.length > 0) {
        activeFilterChips.push({
            key: 'program',
            label: buildFilterButtonLabel('Program', programOptions, programFilters),
            onClear: () => {
                setPage(1);
                setProgramFilters([]);
            },
        });
    }
    if (providerFilters.length > 0) {
        activeFilterChips.push({
            key: 'provider',
            label: buildFilterButtonLabel('Provider', providerOptions, providerFilters),
            onClear: () => {
                setPage(1);
                setProviderFilters([]);
            },
        });
    }
    if (facilityFilters.length > 0) {
        activeFilterChips.push({
            key: 'facility',
            label: buildFilterButtonLabel('Facility', facilityOptions, facilityFilters),
            onClear: () => {
                setPage(1);
                setFacilityFilters([]);
            },
        });
    }
    if (nurseSearchText.trim()) {
        activeFilterChips.push({
            key: 'nurse',
            label: `Nurse: ${nurseSearchText.trim()}`,
            onClear: () => {
                setPage(1);
                setNurseSearchText('');
            },
        });
    }
    const canManageMedication = (_participant: MedcallParticipantItem) => Boolean(bootstrap.hasSuperAccess);
    const openParticipant = (participant: MedcallParticipantItem) => {
        navigate(`${routeBase}/participants/${participant.id}`, {
            state: {
                from: scope === 'my' ? `${routeBase}/my-clients` : `${routeBase}/all-clients`,
            },
        });
    };
    const openAddMedication = (participant: MedcallParticipantItem) => {
        navigate(`${routeBase}/participants/${participant.id}`, {
            state: {
                from: `${routeBase}/my-clients`,
                openDialog: 'medication',
            },
        });
    };
    const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
        setPage(1);
        setSortDirection(direction);
        setSortField(key);
    };
    const renderClientRowActions = (row: MedcallParticipantItem) => {
        const isOnMyClients = (row.view_user_ids || []).includes(`${bootstrap.medcallUserId}`);
        const isPending = pendingParticipantId === row.id;
        const rowCanManageMedication = canManageMedication(row);
        return (<MedcallClientRowActions canWrite={bootstrap.hasSuperAccess} canManageMedication={rowCanManageMedication} isOnMyClients={isOnMyClients} onAddMedication={() => openAddMedication(row)} onAddToMyClients={() => {
                void handleAddToMyClients(row.id);
            }} onOpen={() => openParticipant(row)} onRemoveFromMyClients={() => setRemoveTarget(row)} onSendMessage={() => setMessageTarget(row)} participantId={row.id} pending={isPending} scope={scope}/>);
    };
    if (error) {
        return (<div className="rounded-[var(--border-radius-medium)] border p-4 text-sm" style={{
                backgroundColor: 'var(--semantic-error-soft)',
                borderColor: 'var(--semantic-error-border-soft)',
                color: 'var(--semantic-error)',
            }}>
        {error.message}
      </div>);
    }
    return (<div className={'space-y-3'}>
      {null}
      {null}
      {(<Card>
        <CardContent className="space-y-3 p-[var(--panel-padding)]">
          <div className="flex flex-wrap items-center gap-y-2 [&>div+div]:ml-4 [&>div+div]:border-l [&>div+div]:border-[var(--neutral-gray-60)] [&>div+div]:pl-4">
            <div className="flex items-center gap-2.5 whitespace-nowrap">
              <div className="text-sm font-medium text-[var(--neutral-gray-100)]">View</div>
              <div className="inline-flex items-center rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] p-1">
                <Button aria-pressed={scope === 'my'} className={`h-7 px-3 text-sm shadow-none ${scope === 'my'
                ? 'bg-white text-[var(--neutral-gray-120)] hover:bg-white'
                : 'text-[var(--neutral-gray-100)] hover:bg-white'}`} onClick={() => handleScopeChange('my')} size="sm" type="button" variant="ghost">
                  My Clients
                </Button>
                <Button aria-pressed={scope === 'all'} className={`h-7 px-3 text-sm shadow-none ${scope === 'all'
                ? 'bg-white text-[var(--neutral-gray-120)] hover:bg-white'
                : 'text-[var(--neutral-gray-100)] hover:bg-white'}`} onClick={() => handleScopeChange('all')} size="sm" type="button" variant="ghost">
                  All Clients
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2.5 whitespace-nowrap">
              <div className="text-sm font-medium text-[var(--neutral-gray-100)]">Status</div>
              <Select onValueChange={(value: 'active' | 'all') => {
                setPage(1);
                setStatus(value);
            }} value={status}>
                <SelectTrigger className="h-8 w-[9rem]">
                  <SelectValue placeholder="Status"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <MultiSelectFilter label="Program" onChange={(values) => {
                setPage(1);
                setProgramFilters(values);
            }} options={programOptions} selectedValues={programFilters}/>

            <MultiSelectFilter label="Provider" onChange={(values) => {
                setPage(1);
                setProviderFilters(values);
            }} options={providerOptions} selectedValues={providerFilters}/>

            <MultiSelectFilter label="Facility" onChange={(values) => {
                setPage(1);
                setFacilityFilters(values);
            }} options={facilityOptions} selectedValues={facilityFilters}/>

            <Input className="h-8 w-44" onChange={(event) => {
                setPage(1);
                setNurseSearchText(event.target.value);
            }} placeholder="Nurse" value={nurseSearchText}/>
          </div>

          {activeFilterChips.length > 0 ? (<div className="flex flex-wrap items-center gap-2 border-t border-[var(--neutral-gray-30)] pt-3">
              {activeFilterChips.map((chip) => (<Button key={chip.key} className="h-8 border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] text-[var(--neutral-gray-100)] hover:bg-[var(--neutral-gray-20)]" onClick={chip.onClear} size="sm" type="button" variant="outline">
                  <X className="h-3.5 w-3.5"/>
                  <span>{chip.label}</span>
                </Button>))}
            </div>) : null}
        </CardContent>
      </Card>)}

      <div className="medcall-workspace-client-list__desktop hidden md:block">
      {(<UnifiedTable<MedcallParticipantItem> className="h-[60vh]" searchEnabled clientSearch={false} clientSort={false} columns={columns} data={items} density="compact" emptyDescription="Try changing search or filters." emptyTitle="No clients found" emptyAction={searchText.trim() || hasActiveFilters ? (<div className="flex items-center justify-center gap-2">
              {searchText.trim() ? (<Button className="h-8" onClick={() => {
                        setSearchDraft('');
                        handleSearchSubmit('');
                    }} size="sm" type="button" variant="outline">
                  <X className="h-3.5 w-3.5"/>
                  Clear search
                </Button>) : null}

              {hasActiveFilters ? (<Button className="h-8" onClick={handleReset} size="sm" type="button" variant="outline">
                  <RotateCcw className="h-3.5 w-3.5"/>
                  Reset filters
                </Button>) : null}
            </div>) : null} getRowStyle={(row) => {
                if (row.active) {
                    return undefined;
                }
                return {
                    backgroundColor: 'var(--surface-muted)',
                    color: 'var(--neutral-gray-80)',
                };
            }} loading={loading} onSearchSubmit={handleSearchSubmit} onSearchValueChange={setSearchDraft} searchPlaceholder="Search clients" searchValue={searchDraft} onSortChange={handleSortChange} toolbarRight={<div className="flex flex-wrap items-center justify-end gap-2">
              {scope === 'all' && bootstrap.hasSuperAccess ? (<Button className="h-8" onClick={() => setIntakeDialogOpen(true)} size="sm" type="button" variant="default">
                <Plus className="h-3.5 w-3.5"/>
                  Add Client
                </Button>) : null}

              {bootstrap.hasSuperAccess && (<MedcallOperatorReportsMenu canViewMonitoringReports={bootstrap.canViewMonitoringReports} facilityFilters={facilityFilters} facilityOptions={facilityOptions} nurseSearchText={nurseSearchText} onResetFilters={handleReset} programFilters={programFilters} programOptions={programOptions} providerFilters={providerFilters} providerOptions={providerOptions} routeBase={routeBase} scope={scope}/>)}

            {hasActiveFilters ? (<Button className="h-8" onClick={handleReset} size="sm" type="button" variant="outline">
                <RotateCcw className="h-3.5 w-3.5"/>
                Reset
              </Button>) : null}

            <Button className="h-8" onClick={handleRefresh} size="sm" type="button" variant="outline">
              <RefreshCw className="h-3.5 w-3.5"/>
              Refresh
            </Button>
          </div>} renderRowActions={renderClientRowActions} rowActionsHeader="Action" rowKey={(row) => `${row.id}`} showRowActions sortDirection={sortDirection} sortKey={sortField}/>)}
      </div>

      <div className="medcall-workspace-client-list__mobile md:hidden">
        <MedcallWorkspaceMobileClients canWrite={bootstrap.hasSuperAccess} canManageMedication={canManageMedication} currentUserId={bootstrap.medcallUserId} emptyText={undefined} loading={loading} onAddMedication={openAddMedication} onAddToMyClients={(participant) => {
            void handleAddToMyClients(participant.id);
        }} onOpen={openParticipant} onRemoveFromMyClients={setRemoveTarget} onSendMessage={setMessageTarget} participants={items} pendingParticipantId={pendingParticipantId} scope={scope}/>
      </div>

      {(<div className="medcall-workspace-client-list__pagination flex flex-wrap items-center justify-between gap-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] px-3 py-2">
          <span className="text-sm text-[var(--neutral-gray-100)]">
            {rangeStart}-{rangeEnd} of {totalCount}{''}
          </span>

          <div className="flex items-center gap-1">
          <Button aria-label="Previous page" className="h-8 w-8 p-0" disabled={!canGoBack} onClick={handlePreviousPage} size="sm" type="button" variant="outline">
            <ChevronLeft className="h-4 w-4"/>
          </Button>
          <span className="min-w-[80px] text-center text-sm text-[var(--neutral-gray-100)]">
            {page} / {totalPages}
          </span>
          <Button aria-label="Next page" className="h-8 w-8 p-0" disabled={!canGoNext} onClick={handleNextPage} size="sm" type="button" variant="outline">
            <ChevronRight className="h-4 w-4"/>
          </Button>
          </div>
        </div>)}

      <YesNoDialog cancelLabel="Cancel" confirmClassName="border-[var(--semantic-error)] bg-[var(--semantic-error)] hover:opacity-90" confirmLabel="Remove" confirmDisabled={pendingParticipantId !== null} description={removeTarget
            ? `Remove ${removeTarget.firstname || ''} ${removeTarget.lastname || ''} from your client list?`
            : 'Remove this client from your client list?'} onConfirm={handleConfirmRemove} onOpenChange={(open) => {
            if (open) {
                return;
            }
            if (pendingParticipantId !== null) {
                return;
            }
            setRemoveTarget(null);
        }} open={Boolean(removeTarget)} title="Remove Client"/>
      {(<MedcallParticipantIntakeDialog onCreated={async () => {
                await refetch();
            }} onOpenChange={setIntakeDialogOpen} open={intakeDialogOpen} routeBase={routeBase}/>)}
      {messageTarget ? (<MedcallSendMessageDialog onOpenChange={(open) => {
                if (!open) {
                    setMessageTarget(null);
                }
            }} open participant={messageTarget} participantId={messageTarget.id} preserveLegacySemantics={false}/>) : null}
    </div>);
};
